import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AutoComplete,
  Button,
  Image,
  Input,
  InputNumber,
  Modal,
  Space,
  Spin,
  Typography,
  Upload,
  message,
} from 'antd';
import AdaptiveTable from './AdaptiveTable';
import { DeleteOutlined, InboxOutlined, PlusOutlined } from '@ant-design/icons';
import {
  shipmentApi,
  type ManualMeasurementConfirmResultDTO,
  type ManualMeasurementItem,
  type ManualMeasurementRecordDTO,
  type ManualMeasurementTotals,
} from '../api/kfc';

const MAX_IMAGES = 6;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const MAX_TEXT = 10000;
const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const POLL_INTERVAL_MS = 3000;
const POLL_MAX_MS = 6 * 60 * 1000;

interface PendingImage {
  uid: string;
  file: File;
  url: string;
}

interface EditableRow extends ManualMeasurementItem {
  key: string;
}

interface ManualMeasurementModalProps {
  open: boolean;
  shipmentId: string;
  onClose: () => void;
  onConfirmed: (result: ManualMeasurementConfirmResultDTO) => void;
}

let rowSeq = 0;
const newRow = (item?: ManualMeasurementItem): EditableRow => ({
  key: `r${++rowSeq}`,
  cartonId: item?.cartonId ?? '',
  lengthCm: item?.lengthCm ?? null,
  widthCm: item?.widthCm ?? null,
  heightCm: item?.heightCm ?? null,
  grossWeightKg: item?.grossWeightKg ?? null,
  volumeWeightKg: item?.volumeWeightKg ?? null,
  volumeCbm: item?.volumeCbm ?? null,
});

const positive = (v?: number | null) => (v != null && v > 0 ? v : null);

const round = (v: number, digits: number) => Math.round(v * 10 ** digits) / 10 ** digits;

/**
 * 手动上传代理测量数据：文字 + 图片 → 大模型解析（后端异步，前端轮询）→ 预览修正 → 确认。
 */
const ManualMeasurementModal: React.FC<ManualMeasurementModalProps> = ({ open, shipmentId, onClose, onConfirmed }) => {
  const [text, setText] = useState('');
  const [images, setImages] = useState<PendingImage[]>([]);
  const [record, setRecord] = useState<ManualMeasurementRecordDTO | null>(null);
  const [parsing, setParsing] = useState(false);
  const [rows, setRows] = useState<EditableRow[]>([]);
  const [totals, setTotals] = useState<ManualMeasurementTotals>({});
  const [confirming, setConfirming] = useState(false);
  const [boundCartonIds, setBoundCartonIds] = useState<string[]>([]);
  const pollTimerRef = useRef<number | null>(null);
  const imagesRef = useRef<PendingImage[]>([]);
  imagesRef.current = images;

  const stopPolling = () => {
    if (pollTimerRef.current != null) {
      window.clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  };

  const reset = useCallback(() => {
    stopPolling();
    imagesRef.current.forEach((img) => URL.revokeObjectURL(img.url));
    setText('');
    setImages([]);
    setRecord(null);
    setParsing(false);
    setRows([]);
    setTotals({});
    setConfirming(false);
    setBoundCartonIds([]);
  }, []);

  useEffect(() => {
    if (!open) reset();
  }, [open, reset]);

  useEffect(() => () => stopPolling(), []);

  const addFiles = useCallback((files: File[]) => {
    const current = imagesRef.current;
    const accepted: PendingImage[] = [];
    for (const f of files) {
      if (!ALLOWED_TYPES.includes(f.type)) {
        message.warning(`图片格式仅支持 png / jpg / webp：${f.name || '剪贴板图片'}`);
        continue;
      }
      if (f.size > MAX_IMAGE_BYTES) {
        message.warning(`单张图片不能超过 5MB：${f.name || '剪贴板图片'}`);
        continue;
      }
      if (current.length + accepted.length >= MAX_IMAGES) {
        message.warning(`图片最多 ${MAX_IMAGES} 张`);
        break;
      }
      accepted.push({ uid: `${Date.now()}-${Math.random()}`, file: f, url: URL.createObjectURL(f) });
    }
    if (accepted.length) setImages([...current, ...accepted]);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onPaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const target = e.target as HTMLElement | null;
      const inTextField = !!target && (target.tagName === 'TEXTAREA' || target.tagName === 'INPUT');
      // Excel 等复制内容同时带文字和图片：在输入框内粘贴时按文字处理
      if (inTextField && Array.from(items).some((it) => it.kind === 'string' && it.type === 'text/plain')) return;
      const files: File[] = [];
      let n = imagesRef.current.length;
      for (let i = 0; i < items.length; i++) {
        const it = items[i];
        if (it.kind === 'file' && it.type.startsWith('image/')) {
          const blob = it.getAsFile();
          if (!blob) continue;
          n += 1;
          const ext = blob.type === 'image/jpeg' ? 'jpg' : blob.type === 'image/webp' ? 'webp' : 'png';
          files.push(new File([blob], blob.name && blob.name !== 'image.png' ? blob.name : `paste-${n}.${ext}`, { type: blob.type }));
        }
      }
      if (files.length) {
        e.preventDefault();
        addFiles(files);
      }
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, addFiles]);

  const removeImage = (uid: string) => {
    setImages((prev) => {
      const target = prev.find((p) => p.uid === uid);
      if (target) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.uid !== uid);
    });
  };

  const applyParsedRecord = (rec: ManualMeasurementRecordDTO) => {
    setRecord(rec);
    const bound = rec.boundCartonIds ?? [];
    setBoundCartonIds(bound);
    if (rec.parseError) {
      setRows((prev) => (prev.length ? prev : [newRow()]));
      return;
    }
    const parsed = rec.parsedItems ?? [];
    const fillBound = bound.length > 0 && bound.length === parsed.length;
    setRows(
      parsed.map((it, i) =>
        newRow({ ...it, cartonId: it.cartonId || (fillBound ? bound[i] : '') }),
      ),
    );
    setTotals({
      grossWeightKg: rec.parsedTotals?.grossWeightKg ?? null,
      volumeCbm: rec.parsedTotals?.volumeCbm ?? null,
    });
  };

  const poll = (recordId: string, startedAt: number) => {
    pollTimerRef.current = window.setTimeout(() => {
      shipmentApi
        .getManualMeasurement(shipmentId, recordId)
        .then((res) => {
          const rec = res.data;
          if (rec.status === 'PARSING') {
            if (Date.now() - startedAt > POLL_MAX_MS) {
              setParsing(false);
              message.error('解析超时，可稍后在「上传记录」查看结果，或手工填写后确认');
              return;
            }
            poll(recordId, startedAt);
            return;
          }
          setParsing(false);
          applyParsedRecord(rec);
          if (rec.parseError) {
            message.error('解析失败，可在下方表格手工填写后确认');
          } else {
            message.success(`解析完成：${rec.parsedItems?.length ?? 0} 箱`);
          }
        })
        .catch(() => poll(recordId, startedAt));
    }, POLL_INTERVAL_MS);
  };

  const onParse = () => {
    if (!text.trim() && images.length === 0) {
      message.warning('请输入测量文字或上传测量图片（至少一项）');
      return;
    }
    if (text.length > MAX_TEXT) {
      message.warning(`测量文字不能超过 ${MAX_TEXT} 字`);
      return;
    }
    stopPolling();
    setParsing(true);
    shipmentApi
      .parseManualMeasurement(shipmentId, text, images.map((i) => i.file))
      .then((res) => {
        setRecord(res.data);
        setBoundCartonIds(res.data.boundCartonIds ?? []);
        poll(res.data.id, Date.now());
      })
      .catch((e) => {
        setParsing(false);
        message.error(e.response?.data?.message || '提交解析失败');
      });
  };

  const updateRow = (key: string, patch: Partial<EditableRow>) => {
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const summary = useMemo(() => {
    let weight = 0;
    let volume = 0;
    rows.forEach((r) => {
      weight += positive(r.grossWeightKg) ?? 0;
      const v = positive(r.volumeCbm)
        ?? (positive(r.lengthCm) && positive(r.widthCm) && positive(r.heightCm)
          ? (r.lengthCm! * r.widthCm! * r.heightCm!) / 1_000_000
          : 0);
      volume += v;
    });
    return { weight: round(weight, 4), volume: round(volume, 6) };
  }, [rows]);

  const rowErrors = useMemo(
    () =>
      rows
        .map((r, i) =>
          positive(r.lengthCm) && positive(r.widthCm) && positive(r.heightCm) && positive(r.grossWeightKg)
            ? null
            : i + 1,
        )
        .filter((v): v is number => v != null),
    [rows],
  );

  const canConfirm = !parsing && rows.length > 0 && rowErrors.length === 0;

  const onConfirm = () => {
    if (!canConfirm) return;
    const items: ManualMeasurementItem[] = rows.map(({ key, ...rest }) => ({
      ...rest,
      cartonId: rest.cartonId?.trim() || null,
    }));
    const body = {
      items,
      totals: { grossWeightKg: positive(totals.grossWeightKg), volumeCbm: positive(totals.volumeCbm) },
    };
    setConfirming(true);
    const req = record && record.status === 'PARSED'
      ? shipmentApi.confirmManualMeasurement(shipmentId, record.id, body)
      : shipmentApi.confirmManualMeasurementDirect(shipmentId, text, images.map((i) => i.file), items, body.totals);
    req
      .then((res) => {
        onConfirmed(res.data);
        onClose();
      })
      .catch((e) => message.error(e.response?.data?.message || '确认失败'))
      .finally(() => setConfirming(false));
  };

  const numberCell = (field: keyof ManualMeasurementItem, precision: number, width = 88) =>
    function NumberCell(_: unknown, r: EditableRow) {
      const v = r[field] as number | null | undefined;
      return (
        <InputNumber
          size="small"
          min={0}
          precision={precision}
          style={{ width }}
          value={v ?? null}
          onChange={(val) => updateRow(r.key, { [field]: val ?? null } as Partial<EditableRow>)}
        />
      );
    };

  const cartonOptions = boundCartonIds.map((id) => ({ value: id }));

  return (
    <Modal
      title="上传测量数据"
      open={open}
      onCancel={onClose}
      width={980}
      maskClosable={false}
      destroyOnClose
      footer={
        <Space>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" disabled={!canConfirm} loading={confirming} onClick={onConfirm}>
            确认
          </Button>
        </Space>
      }
    >
      <Spin spinning={parsing} tip="大模型解析中，约需 30–90 秒…">
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>测量文字</div>
          <Input.TextArea
            rows={4}
            value={text}
            maxLength={MAX_TEXT}
            showCount
            disabled={parsing}
            placeholder="粘贴代理发来的测量文字（箱号、长宽高、重量等）"
            onChange={(e) => setText(e.target.value)}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontWeight: 500, marginBottom: 6 }}>
            测量图片
            <Typography.Text type="secondary" style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
              最多 {MAX_IMAGES} 张，单张 ≤ 5MB，png / jpg / webp；可选择、拖入，或在对话框内 Ctrl+V 粘贴截图（已选 {images.length} 张）
            </Typography.Text>
          </div>
          <Upload.Dragger
            multiple
            accept=".png,.jpg,.jpeg,.webp"
            showUploadList={false}
            disabled={parsing || images.length >= MAX_IMAGES}
            beforeUpload={(file, fileList) => {
              if (file === fileList[0]) addFiles(fileList as unknown as File[]);
              return false;
            }}
            style={{ padding: '4px 0' }}
          >
            <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
              <InboxOutlined />
            </p>
            <p className="ant-upload-text" style={{ fontSize: 13 }}>点击或拖入图片；截图可直接 Ctrl+V 粘贴</p>
          </Upload.Dragger>
          {images.length > 0 && (
            <Image.PreviewGroup>
              <Space wrap style={{ marginTop: 8 }}>
                {images.map((img) => (
                  <div key={img.uid} style={{ position: 'relative' }}>
                    <Image src={img.url} width={88} height={88} style={{ objectFit: 'cover', borderRadius: 4 }} />
                    {!parsing && (
                      <Button
                        size="small"
                        danger
                        type="primary"
                        shape="circle"
                        icon={<DeleteOutlined />}
                        style={{ position: 'absolute', top: -6, right: -6 }}
                        onClick={() => removeImage(img.uid)}
                      />
                    )}
                  </div>
                ))}
              </Space>
            </Image.PreviewGroup>
          )}
        </div>

        <Space style={{ marginBottom: 12 }}>
          <Button type="primary" ghost loading={parsing} onClick={onParse}>
            {record ? '重新解析' : '解析'}
          </Button>
          {record?.llmModel && !parsing && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              模型 {record.llmModel}
              {record.llmReasoningEffort ? ` · 推理 ${record.llmReasoningEffort}` : ''}
              {record.parseDurationMs != null ? ` · 耗时 ${(record.parseDurationMs / 1000).toFixed(1)}s` : ''}
            </Typography.Text>
          )}
        </Space>

        {record?.parseError && !parsing && (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 12 }}
            message="解析失败，可在下方表格手工填写后确认"
            description={record.parseError}
          />
        )}
        {!!record?.conflicts?.length && !parsing && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="文字与图片存在不一致（已按图片为准，请核对）"
            description={
              <ul style={{ paddingLeft: 18, margin: 0 }}>
                {record.conflicts.map((c, i) => (
                  <li key={i}>
                    {c.field}：文字 {c.textValue || '-'}，图片 {c.imageValue || '-'}
                    {c.note ? `（${c.note}）` : ''}
                  </li>
                ))}
              </ul>
            }
          />
        )}
        {record?.notes && !parsing && (
          <Alert type="info" showIcon style={{ marginBottom: 12 }} message={record.notes} />
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontWeight: 500 }}>
            预览（{rows.length} 箱）
            <Typography.Text type="secondary" style={{ fontWeight: 400, marginLeft: 8, fontSize: 12 }}>
              长宽高 cm、重量 kg、体积 m³；长、宽、高、毛重必填
            </Typography.Text>
          </span>
          <Button size="small" icon={<PlusOutlined />} disabled={parsing} onClick={() => setRows((p) => [...p, newRow()])}>
            新增行
          </Button>
        </div>
        <AdaptiveTable<EditableRow>
          size="small"
          rowKey="key"
          pagination={false}
          dataSource={rows}
          scroll={{ x: 860, y: 280 }}
          locale={{ emptyText: '解析后在此预览；也可点「新增行」手工填写' }}
          columns={[
            { title: '#', width: 40, render: (_: unknown, __: EditableRow, i: number) => i + 1 },
            {
              title: '箱号',
              width: 170,
              render: (_: unknown, r: EditableRow) => (
                <AutoComplete
                  size="small"
                  style={{ width: 155 }}
                  options={cartonOptions}
                  value={r.cartonId ?? ''}
                  placeholder="空则自动编号"
                  onChange={(v) => updateRow(r.key, { cartonId: v })}
                  filterOption={(input, option) =>
                    String(option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                />
              ),
            },
            { title: '长', width: 96, render: numberCell('lengthCm', 1) },
            { title: '宽', width: 96, render: numberCell('widthCm', 1) },
            { title: '高', width: 96, render: numberCell('heightCm', 1) },
            { title: '毛重', width: 96, render: numberCell('grossWeightKg', 2) },
            { title: '体积重', width: 96, render: numberCell('volumeWeightKg', 2) },
            { title: '体积', width: 110, render: numberCell('volumeCbm', 4, 100) },
            {
              title: '',
              width: 44,
              render: (_: unknown, r: EditableRow) => (
                <Button
                  size="small"
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  disabled={parsing}
                  onClick={() => setRows((p) => p.filter((x) => x.key !== r.key))}
                />
              ),
            },
          ]}
        />
        {rowErrors.length > 0 && (
          <Typography.Text type="danger" style={{ fontSize: 12 }}>
            第 {rowErrors.join('、')} 行长、宽、高、毛重须大于 0
          </Typography.Text>
        )}
        <Space wrap style={{ marginTop: 10 }}>
          <span>件数 {rows.length}</span>
          <span>
            运单毛重(kg)：
            <InputNumber
              size="small"
              min={0}
              precision={2}
              style={{ width: 110 }}
              placeholder={summary.weight ? String(summary.weight) : '按箱汇总'}
              value={totals.grossWeightKg ?? null}
              onChange={(v) => setTotals((t) => ({ ...t, grossWeightKg: v ?? null }))}
            />
          </span>
          <span>
            运单体积(m³)：
            <InputNumber
              size="small"
              min={0}
              precision={4}
              style={{ width: 110 }}
              placeholder={summary.volume ? String(summary.volume) : '按箱汇总'}
              value={totals.volumeCbm ?? null}
              onChange={(v) => setTotals((t) => ({ ...t, volumeCbm: v ?? null }))}
            />
          </span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            逐箱合计：{summary.weight} kg / {summary.volume} m³；汇总留空则按逐箱合计
          </Typography.Text>
        </Space>
      </Spin>
    </Modal>
  );
};

export default ManualMeasurementModal;
